import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';

const milestones = [
  { year: '2018', title: '平台创立', description: '一起草.com 正式上线，致力于为团队提供高效在线文稿协作解决方案。' },
  { year: '2019', title: '用户突破10万', description: '凭借出色的产品体验，注册用户快速增长至10万，服务覆盖全国主要城市。' },
  { year: '2020', title: '企业版发布', description: '推出企业版协作平台，为大型组织提供私有化部署和定制化功能。' },
  { year: '2021', title: '用户突破50万', description: '累计服务超过50万用户、3万个团队，平台文档数量突破100万。' },
  { year: '2022', title: '获得A轮融资', description: '完成A轮融资，加速产品研发和市场拓展，团队规模扩大至200人。' },
  { year: '2023', title: 'AI智能助手上线', description: '推出AI智能写作辅助功能，帮助用户更高效地完成文稿创作和审核。' },
  { year: '2024', title: '用户突破50万活跃', description: '月度活跃用户突破50万，成为国内领先的在线文稿协作平台。' },
];

const values = [
  {
    icon: 'ri-focus-2-line',
    title: '用户至上',
    description: '始终以用户需求为导向，持续优化产品体验，让每一处细节都体现对用户的关怀。',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: '技术创新',
    description: '持续投入技术研发，将最新的AI和云计算技术融入产品，为用户提供最先进的协作体验。',
  },
  {
    icon: 'ri-shield-star-line',
    title: '安全可靠',
    description: '将数据安全放在首位，采用业界领先的安全技术，确保您的每一份文档都得到最严格的保护。',
  },
  {
    icon: 'ri-hand-heart-line',
    title: '合作共赢',
    description: '与用户、合作伙伴共同成长，建立开放共赢的生态系统，推动在线协作行业的发展。',
  },
];

const certifications = [
  { icon: 'ri-shield-check-line', name: 'ISO 27001 信息安全认证' },
  { icon: 'ri-lock-line', name: '国家信息安全等级保护三级' },
  { icon: 'ri-cloud-line', name: '可信云服务认证' },
  { icon: 'ri-award-line', name: '2024年度最佳SaaS产品奖' },
];

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '一起草.com',
  url: 'https://szarssm.cn/',
  description: '专业在线文稿协作平台，支持多人实时共同起草文件、合同、方案与创意内容。',
  foundingDate: '2018',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-XXX-XXXX',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
    email: 'support@yicao.com',
  },
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'CN',
    addressLocality: '北京',
    addressRegion: '北京市',
    streetAddress: '中关村科技园区',
  },
  sameAs: [
    'https://weibo.com/yicao',
    'https://www.zhihu.com/org/yicao',
  ],
};

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于一起草.com',
  description: '了解一起草.com的品牌故事、发展历程、核心价值与团队信息。',
};

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d9u10lv8l97n3eas3ffg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = (parsed as Record<string, unknown>)?.meta && typeof (parsed as Record<string, unknown>).meta === 'object'
          ? ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message as string || ''
          : '';
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们"
        description="了解一起草.com的品牌故事、发展历程、核心价值与团队信息。专业在线文稿协作平台，用技术让协作更高效。"
        keywords="一起草,关于我们,品牌介绍,公司简介,在线协作"
        canonicalUrl="https://szarssm.cn/about"
        schema={{ ...orgSchema, ...aboutSchema }}
      />

      <Navbar />

      <main className="pt-16">
        {/* Hero */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                关于一起草<span className="text-primary-500">.com</span>
              </h1>
              <p className="mt-6 text-lg text-foreground-600 leading-relaxed">
                我们是一家专注于在线文稿协作的技术公司。自2018年创立以来，始终致力于通过技术创新让团队协作写作变得更加高效、顺畅。今天，已有超过50万用户选择一起草.com作为他们的协作写作平台。
              </p>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-8 md:p-10 rounded-xl bg-background-50">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-5">
                  <i className="ri-flag-line text-xl"></i>
                </div>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">我们的使命</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  让每一份文稿的创作过程都变得简单、高效、愉悦。我们相信，优秀的协作工具可以激发团队的创造潜能，让好想法更快变成好作品。无论是合同起草、方案策划还是创意写作，一起草.com都致力于成为您最信赖的协作伙伴。
                </p>
              </div>
              <div className="p-8 md:p-10 rounded-xl bg-background-50">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-5">
                  <i className="ri-eye-line text-xl"></i>
                </div>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">我们的愿景</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为全球最受欢迎的在线文稿协作平台。我们希望通过持续的技术创新和产品打磨，让协作写作成为每个人都能轻松掌握的技能，打破时间和空间的限制，让创造力的流动永不停歇。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                核心价值观
              </h2>
              <p className="mt-4 text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                这些价值观塑造了我们的产品，也指导着我们的每一个决策。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((item, index) => (
                <div key={index} className="text-center p-6 rounded-xl bg-background-100">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                发展历程
              </h2>
              <p className="mt-4 text-foreground-600">
                从最初的想法到今天的成就，每一步都值得铭记。
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2"></div>
              <div className="space-y-10">
                {milestones.map((item, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col md:flex-row gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="md:px-8">
                        <div className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold mb-2">
                          {item.year}
                        </div>
                        <h3 className="text-lg font-heading font-semibold text-foreground-900">{item.title}</h3>
                        <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-center flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-primary-500 border-4 border-background-100 z-10"></div>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-16 md:py-20 bg-accent-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
                资质与认证
              </h2>
              <p className="mt-4 text-accent-100 max-w-2xl mx-auto leading-relaxed">
                多项权威认证，为您的数据安全保驾护航。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {certifications.map((item, index) => (
                <div key={index} className="text-center p-6 rounded-xl bg-background-50/10 backdrop-blur-sm">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-50/20 text-background-50 mb-3">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <p className="text-sm text-background-50/90 font-medium">{item.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950">
                  联系我们
                </h2>
                <p className="mt-4 text-foreground-600 leading-relaxed">
                  无论您有任何问题、建议或合作意向，都欢迎与我们取得联系。我们的团队将尽快回复您。
                </p>

                <div className="mt-8 space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">电子邮箱</div>
                      <p className="text-sm text-foreground-600 mt-1">support@yicao.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">客服电话</div>
                      <p className="text-sm text-foreground-600 mt-1">400-XXX-XXXX</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">办公地址</div>
                      <p className="text-sm text-foreground-600 mt-1">北京市海淀区中关村科技园区</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">工作时间</div>
                      <p className="text-sm text-foreground-600 mt-1">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-8 rounded-xl bg-background-100">
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">发送消息</h3>
                <form className="space-y-4" data-readdy-form onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您想说的内容（最多500字）"
                    ></textarea>
                  </div>
                  <div className="honeypot-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                      消息已发送成功，我们会尽快回复您！
                    </div>
                  )}
                  {formStatus === 'error' && formError && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm text-center">
                      {formError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full px-6 py-3 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}